import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { deviceHasThisSub, subIsActive } from './';
import { billingSubscribe, fetchFromBilling } from './billingAccess';
import { path } from 'ramda';
import { Loader } from '../../components';
import { AwnSwitch } from '../common';
import { Link } from 'react-router-dom';
import './SmsAutoRefillToggle.less';

function SmsAutoRefillToggle({ user, userActions }) {
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const smsIntervalRef = useRef();
  const hasCard = !!path(['info', 'payments', 'defaultPaymentMethod'], user);
  const smsSub = deviceHasThisSub(user, null, 'awn_sms_notifications');
  const smsNotAllowed = smsSub && smsSub.smsSendingIsAllowed === false;

  // Handle SMS toggle
  const handleSmsToggle = async () => {
    // Don't allow toggle if no card on file or SMS not allowed
    if (!hasCard || smsNotAllowed) {
      return;
    }

    const smsSub = deviceHasThisSub(user, null, 'awn_sms_notifications');
    const currentIsActive = smsSub && subIsActive(smsSub) && !smsSub.cancelAtPeriodEnd;
    const userId = path(['info', '_id'], user);

    setSmsLoading(true);
    setSmsError(null);
    
    if (smsSub && subIsActive(smsSub)) {
      // Subscription exists, toggle cancel_at_period_end
      const cancel_at_period_end = currentIsActive; // If currently active, set to cancel
      
      const res = await fetchFromBilling(`/subscriptions?userId=${userId}&macAddress=${smsSub.macAddress || ''}&productName=${smsSub.productName}`, {
        method: 'PUT',
        body: JSON.stringify({
          cancel_at_period_end
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // fetchFromBilling returns parsed JSON, check for error in response
      if (res.error) {
        setSmsLoading(false);
        setSmsError(res.error || 'Failed to update SMS auto-refill');
        return;
      }
      
      // Update local user state
      const userSubs = path(['info', 'payments', 'subscriptions'], user) || [];
      const smsSubIndex = userSubs.findIndex(sub => 
        sub.productName === smsSub.productName && 
        (sub.macAddress === smsSub.macAddress || (!sub.macAddress && !smsSub.macAddress))
      );
      
      if (smsSubIndex !== -1) {
        const newUser = Object.assign({}, user.info);
        newUser.payments.subscriptions[smsSubIndex].cancelAtPeriodEnd = cancel_at_period_end;
        await userActions.refreshUser(newUser);
      }
      
      setSmsLoading(false);
    } else {
      // No active subscription, create new one
      const result = await billingSubscribe(userId, null, 'awn_sms_notifications');
      
      if (!result.success) {
        setSmsLoading(false);
        setSmsError(result.message || 'Failed to update SMS auto-refill');
        return;
      }
      
      // Start refresh interval for new subscription
      smsIntervalRef.current = {
        int: setInterval(() => {
          smsIntervalRef.current.count++;
          userActions.refreshUser();
          
          // Check if we've reached max attempts
          if (smsIntervalRef.current.count >= 10) {
            clearInterval(smsIntervalRef.current.int);
            setSmsLoading(false);
          }
        }, 1500),
        count: 0,
        originalState: currentIsActive
      };
    }
  };
  
  // Check for SMS subscription state changes
  useEffect(() => {
    if (smsIntervalRef.current && smsIntervalRef.current.originalState !== undefined) {
      const smsSub = deviceHasThisSub(user, null, 'awn_sms_notifications');
      const currentIsActive = smsSub && subIsActive(smsSub) && !smsSub.cancelAtPeriodEnd;
      
      // If state changed, stop refreshing
      if (currentIsActive !== smsIntervalRef.current.originalState) {
        clearInterval(smsIntervalRef.current.int);
        setSmsLoading(false);
        smsIntervalRef.current = null;
      }
    }
  }, [user]);

  // Cleanup SMS interval on unmount
  useEffect(() => {
    return () => {
      if (smsIntervalRef.current && smsIntervalRef.current.int) {
        clearInterval(smsIntervalRef.current.int);
      }
    };
  }, []);

  if (smsLoading) {
    return (
      <>
        <div className='sms-toggle-row'>
          <Loader small />
          <span className='sms-toggle-label'>Updating SMS auto-refill...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='sms-toggle-row'>
        <AwnSwitch
          checked={(() => {
            const smsSub = deviceHasThisSub(user, null, 'awn_sms_notifications');
            return smsSub && subIsActive(smsSub) && !smsSub.cancelAtPeriodEnd && smsSub.smsSendingIsAllowed !== false;
          })()}
          onChange={handleSmsToggle}
          disabled={!hasCard || smsNotAllowed}
        />
        <span className='sms-toggle-label'>
          Metered SMS auto-refill*
          {!hasCard && <span className='disabled-text'>(Disabled)</span>}
        </span>
      </div>
      {(!hasCard || smsNotAllowed) && (
        <p className='sms-toggle-error no-card-error'>
          To enable automatic SMS refills, please <Link
            to='/account/subscriptions#payment_details'
            onClick={() => {
              // Small delay to allow navigation before scrolling
              userActions.doModal(null)
              setTimeout(() => {
                const element = document.getElementById('payment_details');
                if (element) {
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  window.scrollTo({
                    top: elementPosition - 160,
                    behavior: 'smooth'
                  });
                }
              }, 100);
            }}
          >add a valid payment method</Link>
        </p>
      )}
      {smsError && (
        <p className='sms-toggle-error'>
          {smsError}
        </p>
      )}
      <p className='sms-toggle-disclaimer'>
        * Automatically add 100 SMS ($1.50 per 100 SMS / Monthly billed) when your available messages are depleted.
      </p>
    </>
  );
}

SmsAutoRefillToggle.propTypes = {
  user: PropTypes.object.isRequired,
  userActions: PropTypes.object.isRequired
};

SmsAutoRefillToggle.displayName = 'SmsAutoRefillToggle';

export default SmsAutoRefillToggle;